import cx from 'classnames';
import * as React from 'react';
import { callAll, isDefined } from '../lib';

export interface CheckboxProps<T>
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type' | 'value'> {
  value: T;
}

export const Checkbox = React.forwardRef<
  HTMLInputElement,
  CheckboxProps<unknown>
>(function Checkbox({ checked: providedChecked, value, ...props }, ref) {
  const contextValue = React.useContext(CheckboxGroupContext);
  const checked = isDefined(providedChecked)
    ? providedChecked
    : contextValue.values.includes(value);
  const name = isDefined(props.name) ? props.name : contextValue.name;

  return (
    <input
      {...props}
      type="checkbox"
      checked={checked}
      name={name}
      onChange={callAll(props.onChange, ev =>
        contextValue.onChangeValues(
          ev.target.checked
            ? contextValue.values.concat(value)
            : contextValue.values.filter(v => v !== value)
        )
      )}
      className={cx('form-checkbox', props.className)}
      ref={ref}
    />
  );
});

export const CheckboxGroup = <T extends unknown>({
  values,
  onChangeValues,
  name,
  children,
}: CheckboxGroupContextType<T> & { children: React.ReactNode }) => {
  return (
    <CheckboxGroupContext.Provider
      value={React.useMemo(
        () =>
          ({
            values,
            onChangeValues,
            name,
          } as any),
        [values, onChangeValues, name]
      )}
    >
      {children}
    </CheckboxGroupContext.Provider>
  );
};

interface CheckboxGroupContextType<T> {
  values: T[];
  onChangeValues: (values: T[]) => void;
  name: string;
}

export const CheckboxGroupContext = React.createContext<
  CheckboxGroupContextType<unknown>
>({
  values: [],
  onChangeValues: function noop() {},
  name: '',
});
